/**
 * @module experience/device-assessment
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
// tslint:disable max-file-line-count
// interfaces
import { GetWarrantyExchange } from '@support/api/proxies/responses/api-2911.interface';
import { Api2134Request, Overrides } from '@support/api/proxies/requests/api-2134.interface';
import {
  Api2128Request,
  DeviceConditionQuestion,
} from '@support/api/proxies/requests/api-2128.interface';

import {
  AssessmentItem,
  SelectedItems,
} from '@experience/order-summary/interfaces/order-summary-new.interface';
import {
  PathParams,
  SelectedAssessmentQuestions,
  ManagerOverrideDetails,
} from '@experience/device-assessment/interfaces/device-assessment.interface';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';

// services
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { Product } from '@support/api/common/product.interface';

// constants
import {
  DEVICE_ASSESSSMENT_CONSTANT,
  CART_METHOD,
} from '@experience/device-assessment/device-assessment.constants';
import { EndpointConfigInterface } from '@core/interfaces/endpoint-configuration.interface';
import { Line } from '@support/api/proxies/responses/api-cart-response.interface';
// import {CHANNELS, SETTINGS} from '@environment/settings';
export class CartRequestPayloadModel {
  /**
   * Stores the value of request payload for remorse exchange
   */
  public requestPayloadRemorseExchange: Api2134Request;

  /**
   * Stores the value of request payload for warranty exchange
   */
  public requestPayloadWarrantyExchange: Api2128Request;

  /**
   * Stores the url of API
   */
  public apiUrl: EndpointConfigInterface;

  /**
   * Stores the value of path param of api
   */
  public apiPathParams: PathParams;

  /**
   * Stores value of line id
   */
  private productType: string;

  /**
   * Stores value of line id
   */
  private lineId: string;
  /**
   * Stores value of cart id
   */
  private cartId: string;
  /**
   * Stores value of selected items
   */
  private items: SelectedItems;
  /**
   * Stores value of assessment type
   */
  private assessmentType: string;
  /**
   * Boolean to check retail channel
   */
  // private isRetail: boolean;

  /* tslint:disable: cyclomatic-complexity */
  constructor(
    private methodType: string,
    private assessmentItem: AssessmentItem,
    private getWarrantyExchangeResponse: GetWarrantyExchange,
    private selectedProduct: Product,
    private deviceAssessmentQuestions: SelectedAssessmentQuestions[],
    private stateStoreUtilsService: StateStoreUtilsService,
    private objectService: ObjectService,
    private selectedReason?: string,
    private idVal?: string,
    private skuValue?: string,
    private replacedProduct?: Product,
    private unsellableReasonCode?: string,
    private unsellableReasonDescription?: string,
    private isItemSellable?: boolean,
    private licensePlateNumber?: string,
    private managerOverrideDetails?: ManagerOverrideDetails
  ) {
    let offerId: string;
    let sku: string;
    let imei: string;
    let transactionType: string;
    // this.isRetail = SETTINGS.CHANNEL.toLowerCase() === CHANNELS.RETAIL;
    const reasonCode = this.getReason(this.selectedReason);
    this.assessmentType = this.stateStoreUtilsService.getDeviceAssessmentType();
    const isWarranty = this.assessmentType === DEVICE_ASSESSSMENT_CONSTANT.WARRANTY;
    this.cartId = this.stateStoreUtilsService.getCartId() || DEVICE_ASSESSSMENT_CONSTANT.CART_ID;
    this.items = this.stateStoreUtilsService.getSelectedProductForReturn();
    this.lineId =
      this.objectService.get(this.assessmentItem, `lineId`) ||
      this.stateStoreUtilsService.getLineId();
    const modeOfExchange = this.getModeOfExchange(isWarranty);
    if (isWarranty) {
      sku = this.skuValue || this.getWarrantyExchangeResponse.sku;
      imei = this.getWarrantyExchangeResponse.imei;
      offerId = this.idVal || this.getWarrantyExchangeResponse.productOfferingId;
      transactionType = DEVICE_ASSESSSMENT_CONSTANT.EXCHANGE;
      this.requestPayloadWarrantyExchange = {
        accountId: this.stateStoreUtilsService.getAccountId(),
        offerId: offerId,
        sku: sku,
        transactionTypes: [transactionType],
        reason: DEVICE_ASSESSSMENT_CONSTANT.WARRANTY,
        itemRelation: {
          imeiFrom: imei,
          relationshipType: DEVICE_ASSESSSMENT_CONSTANT.EXCHANGE,
        },
        modeOfExchange: modeOfExchange,
        assessmentCartItemId: this.getCartItemId(),
        deviceAssessment: {
          deviceConditionQuestions: this.getdeviceAssessmentAnswers(
            this.deviceAssessmentQuestions,
            this.selectedReason
          ),
          deviceDiagnostics: this.getDeviceDiagnostics(isWarranty),
        },
        reasonCode: reasonCode,
      };
      if (this.managerOverrideDetails) {
        this.requestPayloadWarrantyExchange.overrides = this.getOverrides(isWarranty);
      }
    } else {
      const productType = this.objectService.get(this.selectedProduct, 'productType') || '';
      const originalOrderId = this.objectService.get(
        this.stateStoreUtilsService.getOrderSummaryData(),
        'orderId'
      );
      transactionType = this.objectService.get(this.items, 'type')
        ? this.items.type.toUpperCase()
        : DEVICE_ASSESSSMENT_CONSTANT.RETURN_REASON;

      if (this.replacedProduct) {
        sku = this.objectService.get(this.replacedProduct, 'defaultSku');
        offerId = this.objectService.get(this.replacedProduct, 'familyCode');
      } else {
        sku = this.objectService.get(this.selectedProduct, 'sku');
        offerId = this.objectService.get(this.selectedProduct, 'offerId');
      }
      this.requestPayloadRemorseExchange = {
        offerId: offerId,
        sku: sku,
        transactionTypes: [transactionType],
        reason: DEVICE_ASSESSSMENT_CONSTANT.REMORSE,
        modeOfExchange: modeOfExchange,
        actionCode:
          productType.toUpperCase() === DEVICE_ASSESSSMENT_CONSTANT.DEVICE && this.selectedReason
            ? DEVICE_ASSESSSMENT_CONSTANT.ACTION_UPDATE
            : DEVICE_ASSESSSMENT_CONSTANT.REMOVE,
        reasonCode: reasonCode,
        priceChangedIndicator: '',
        productType: [productType],
        originalOrderLineId: this.objectService.get(this.selectedProduct, 'id'),
        originalOrderId: originalOrderId,
      };

      if (this.deviceAssessmentQuestions || this.selectedReason) {
        this.requestPayloadRemorseExchange.deviceAssessment = {
          deviceConditionQuestions: this.getdeviceAssessmentAnswers(
            this.deviceAssessmentQuestions,
            this.selectedReason
          ),
          deviceDiagnostics: this.getDeviceDiagnostics(isWarranty),
        };
      }

      if (this.assessmentType === DEVICE_ASSESSSMENT_CONSTANT.EXCHANGE) {
        this.requestPayloadRemorseExchange.itemRelation = {
          cartItemIdFrom: this.cartId,
          relationshipType: DEVICE_ASSESSSMENT_CONSTANT.RETURN_REASON,
        };
      }

      if (this.stateStoreUtilsService.getRISStoreState()) {
        this.requestPayloadRemorseExchange.risInventoryDetails = {
          equipmentReturnDetails: {
            reasonCode: this.unsellableReasonCode,
            reasonDescription: this.unsellableReasonDescription,
            isSellable: this.isItemSellable,
            licensePlateNumber: this.licensePlateNumber,
          },
          isSOAMigratedStore: {
            isStoreRIS: this.stateStoreUtilsService.getRISStoreState(),
          },
        };
      }
      if (this.managerOverrideDetails) {
        this.requestPayloadRemorseExchange.overrides = this.getOverrides(isWarranty);
      }
      if (this.methodType === CART_METHOD.PUT) {
        this.requestPayloadRemorseExchange.cartItemId = this.objectService.get(
          this.assessmentItem,
          'cartItemIdFrom'
        );
      }
    }
    this.setApiUrl();
    this.setPathParams(this.selectedReason);
  }

  /**
   * Method to get line Item id
   * @return string
   */
  private getCartItemId(): string {
    const cart = this.stateStoreUtilsService.getCart();
    const lineIdInFocus = this.stateStoreUtilsService.getLineId();
    const lines = this.objectService.get(cart, 'lines');
    if (cart && lines) {
      const selectedLine = lines.find((line: Line) => {
        return line.id === lineIdInFocus;
      });
      return this.objectService.get(selectedLine, 'returningItem.id');
    } else {
      return '';
    }
  }
  /**
   * Method is used get mode.
   * @param isWarranty : boolean
   * @return string
   */
  private getModeOfExchange(isWarranty: boolean): string {
    let mode: string;
    if (isWarranty) {
      mode = DEVICE_ASSESSSMENT_CONSTANT.POST;
    } else {
      // if (this.isRetail) {
      //   mode = DEVICE_ASSESSSMENT_CONSTANT.IMMEDIATE;
      // }
      // else {
      mode = DEVICE_ASSESSSMENT_CONSTANT.POST;
      // }
    }
    return mode;
  }

  /**
   * Method should get Device Static Data by calling api 2912
   * @param imei : string
   * @param sku : string
   * @return Observable<GetDeviceStaticData>
   */
  private getdeviceAssessmentAnswers(
    deviceAssessmentQuestions: SelectedAssessmentQuestions[],
    selectedReason: string
  ): DeviceConditionQuestion[] {
    const deviceConditionQuestions = [];
    if (!selectedReason) {
      for (const question of deviceAssessmentQuestions) {
        const questionAnswer: DeviceConditionQuestion = {
          verificationQuestions: {
            questionId: this.objectService.get(question, 'questionId'),
            answerId: this.objectService.get(question, 'answerId'),
          },
        };
        deviceConditionQuestions.push(questionAnswer);
      }
    }

    return deviceConditionQuestions;
  }

  /**
   * Method is used to check device diagnostic conditions.
   * @param isWarranty : boolean
   * @return string
   */
  private getDeviceDiagnostics(isWarranty: boolean): string {
    const manufacturerName = this.objectService.get(
      this.getWarrantyExchangeResponse,
      'manufacturerName'
    );
    if (manufacturerName) {
      const deviceDiagnosticValue = isWarranty
        ? manufacturerName
          ? manufacturerName.toLocaleLowerCase()
          : manufacturerName
        : this.objectService.get(this.selectedProduct, 'manufacturer');
      return deviceDiagnosticValue === DEVICE_ASSESSSMENT_CONSTANT.APPLE
        ? DEVICE_ASSESSSMENT_CONSTANT.PASSED
        : DEVICE_ASSESSSMENT_CONSTANT.BYPASS;
    } else {
      return DEVICE_ASSESSSMENT_CONSTANT.BYPASS;
    }
  }

  /**
   * Method is used to set cart request path params
   * @param selectedReason : string
   */
  private setPathParams(selectedReason: string): void {
    this.apiPathParams = <PathParams>{};
    if (this.assessmentType !== DEVICE_ASSESSSMENT_CONSTANT.WARRANTY) {
      this.apiPathParams.cartId = this.cartId;
      this.apiPathParams.lineId = this.lineId;
    } else {
      this.apiPathParams.cartId = selectedReason
        ? this.cartId
        : DEVICE_ASSESSSMENT_CONSTANT.CART_ID;
      this.apiPathParams.lineId = this.lineId;
    }
  }

  /**
   * Method is used to set cart request urk
   * @param selectedReason : string
   */
  private setApiUrl(): void {
    this.productType = this.objectService.get(this.assessmentItem, 'assessmentType');
    if (this.assessmentType !== DEVICE_ASSESSSMENT_CONSTANT.WARRANTY) {
      if (
        this.productType &&
        this.productType.toUpperCase() === DEVICE_ASSESSSMENT_CONSTANT.LINE_DEVICE
      ) {
        this.apiUrl =
          this.methodType === CART_METHOD.PUT
            ? API_MAP.API_2135_putRemorseToCartAsLineItem
            : API_MAP.API_2134_postRemorseToCartAsLineItem;
      } else {
        this.apiUrl =
          this.methodType === CART_METHOD.PUT
            ? API_MAP.API_2136_putRemorseToCart
            : API_MAP.API_2137_postRemorseToCart;
      }
    } else {
      this.apiUrl =
        this.methodType === CART_METHOD.PUT
          ? API_MAP.API_2129_putWarrantyToCart
          : API_MAP.API_2128_postWarrantyToCart;
    }
  }

  /**
   * Method is used get mode.
   * @param selectedReason : string
   * @return string
   */
  private getReason(selectedReason: string): string {
    return selectedReason ? selectedReason : '';
  }
  /**
   * Method is used to get overrides payload.
   * @param isWarranty : boolean
   * @return Overrides
   */
  private getOverrides(isWarranty: boolean): Overrides {
    return {
      overrideTransactionId: this.objectService.get(
        this.managerOverrideDetails,
        'overrideTransactionId'
      ),
      overrideIndicator: true,
      activityName: DEVICE_ASSESSSMENT_CONSTANT.OVERRIDE_INELIGIBLE_RETURN,
      overrideDateTime: new Date().toISOString(),
      overrideReason: isWarranty ? '' : DEVICE_ASSESSSMENT_CONSTANT.BYPASS_REMORSE_RETURN_RESTR,
      cartItemId: this.objectService.get(this.managerOverrideDetails, 'cartItemId'),
    };
  }
}
